import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import ModalLayout from '../layouts/ModalLayout';
import { ProfileDataType, ProfileField } from '../../../types/Profile';
import { CustomFieldResponses } from '../../../models/inviteService.model';
import userService from '../../../services/userService';
import UserDataForm from '../forms/userData/UserDataForm';
import CustomFieldsForm from '../forms/CustomFieldsForm';
import Button from '../buttons/Button';
import connectionService from '../../../services/community/connectionService';

interface SharedWithCommunityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function SharedWithCommunityModal({
  open,
  setOpen,
}: SharedWithCommunityModalProps): JSX.Element {
  const { inviteFields: invite, id: communityId } = useSelectedCommunity() ?? {};
  const { t } = useTranslation();
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>({});

  useEffect(() => {
    if (!open || !communityId) return;
    Promise.all([
      userService.getSharedDataWithCommunity(communityId),
      userService.getUserData(),
    ]).then(([sharedData, userData]) => {
      const { propertyIds, customFieldResponses: cfr } = sharedData;
      const sharedDataWithTarget = userData.filter((field) => propertyIds.includes(field.id!));
      const sharedCustomFields = invite?.customFields.reduce((acc, field) => {
        acc[field.customFieldId!] = cfr[field.customFieldId!];
        return acc;
      }, {} as CustomFieldResponses);
      setCustomFieldResponses(sharedCustomFields!);
      setSelectedData(sharedDataWithTarget);
    });
  }, [open]);

  const handleSubmit = () => {
    if (!invite) return;
    const { mandatoryFields } = invite;
    const mandatoryCustom = invite.customFields.filter((f) => f.mandatory);
    if (
      mandatoryCustom.some((f) => !customFieldResponses[f.customFieldId!]) ||
      mandatoryFields.some((f) => !selectedData.find((d) => d.dataType === f))
    ) {
      toast.error(t('toast.error.mandatoryStar'));
      return;
    }
    userService
      .updateCommunityConnection(communityId!, {
        propertyIds: selectedData.map((f) => f.id!),
        customFieldResponses,
        dataForMembers: selectedData.map((f) => f.id!),
      })
      .then(() => {
        setOpen(false);
        connectionService.getConnections(true);
      });
  };

  if (
    open &&
    invite !== undefined &&
    !invite.mandatoryFields.length &&
    !invite.optionalFields.length &&
    !invite.customFields.length
  ) {
    setOpen(false);
    toast.warn(t('page.settings.requested.noFields'));
    return <></>;
  }

  return (
    <ModalLayout
      className="relative z-50 flex w-[45rem] min-w-[300px] flex-col gap-6 rounded-2xl bg-secondary-50 p-8"
      open={open}
      setOpen={setOpen}>
      <UserDataForm
        fetchData={false}
        selectedData={selectedData}
        setSelectedData={setSelectedData}
        onePerDataType
        mandatoryFields={invite?.mandatoryFields ?? []}
        nonRequestedFields={[
          ...(invite?.nonRequestedFields ?? []),
          ProfileDataType.SOCIAL
        ]}
      />
      <CustomFieldsForm
        fields={invite?.customFields ?? []}
        response={customFieldResponses}
        includeMandatory
        setResponse={setCustomFieldResponses}
      />
      <Button onClick={handleSubmit} variant="primary" className="w-full justify-center">
        {t('general.save')}
      </Button>
    </ModalLayout>
  );
}
